import {
  colors,
  getBorderRadius,
  getHeight,
  getSpacing,
} from '@cabonline/ui-shared';
import { Button, Text } from '@cabonline/ui-web';
import styled from '@emotion/styled';
import { WarningAmberOutlined } from '@mui/icons-material';
import { useAuth, useLocale } from '@multiplatform-apps/service-trips';
import React, { FC, useCallback } from 'react';

import { isUnauthorizedError } from '../../utils/errors';
import { ErrorDetailsAccordion } from '../error-details-accordion';

export const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.gray2};
  border-radius: ${getBorderRadius('l')};
  gap: ${getSpacing('xxs')};
  min-height: ${getHeight('statusContentMinHeight')};
  flex-grow: 1;
  padding: ${getSpacing('s')};
  text-align: center;
`;

interface PageErrorProps {
  title?: string;
  message?: string;
  error: Error;
  retry?: () => void;
}

export const PageError: FC<PageErrorProps> = ({
  title,
  message,
  error,
  retry,
}) => {
  const { t } = useLocale();
  const { logout } = useAuth();
  const isUnauthorized = isUnauthorizedError(error);
  const handleSignIn = useCallback(() => logout(), [logout]);

  return (
    <StyledContainer role="alert">
      <WarningAmberOutlined fontSize="large" aria-hidden="true" />
      <Text weight="extraBold" size="xl">
        {isUnauthorized ? t('error.unauthorized.title') : title}
      </Text>
      {isUnauthorized ? (
        <Text color={colors.gray1}>{t('error.unauthorized.description')}</Text>
      ) : (
        message && <Text color={colors.gray1}>{message}</Text>
      )}
      {isUnauthorized ? (
        <Button onClick={handleSignIn} label={t('error.unauthorized.signInButton')} />
      ) : (
        retry && <Button onClick={retry} label={t('common.retry')} />
      )}
      <ErrorDetailsAccordion error={error} />
    </StyledContainer>
  );
};
